import React from 'react';
import { Box, Typography, ButtonBase, Skeleton } from '@mui/material';
import { appColors } from '../../Config/Theme';

// ── Variant color map ──

export type KpiVariant =
  | 'default'
  | 'accounted'
  | 'notAccounted'
  | 'toAccount'
  | 'notChecked'
  | 'fullRoster'
  | 'noBadgeToday';

interface VariantColors {
  iconBg: string;
  icon: string;
  number: string;
  /** Number color when value is 0 and the card should look calm. */
  numberZero: string;
}

const VARIANT_COLORS: Record<KpiVariant, VariantColors> = {
  default: {
    iconBg: `${appColors.primary.main}18`,
    icon: appColors.primary.main,
    number: appColors.text.heading,
    numberZero: appColors.text.caption,
  },
  toAccount: {
    iconBg: `${appColors.primary.main}18`,
    icon: appColors.primary.main,
    number: appColors.primary.dark,
    numberZero: appColors.text.caption,
  },
  accounted: {
    iconBg: `${appColors.status.success}18`,
    icon: appColors.status.success,
    number: appColors.status.success,
    numberZero: appColors.text.caption,
  },
  notAccounted: {
    iconBg: `${appColors.status.error}14`,
    icon: appColors.status.error,
    number: appColors.status.error,
    numberZero: appColors.text.caption,
  },
  notChecked: {
    iconBg: `${appColors.text.caption}18`,
    icon: appColors.text.body,
    number: appColors.text.heading,
    numberZero: appColors.text.caption,
  },
  fullRoster: {
    iconBg: `${appColors.text.caption}18`,
    icon: appColors.text.body,
    number: appColors.text.heading,
    numberZero: appColors.text.caption,
  },
  noBadgeToday: {
    iconBg: `${appColors.text.caption}18`,
    icon: appColors.text.body,
    number: appColors.text.heading,
    numberZero: appColors.text.caption,
  },
};

// ── Props ──

export interface KpiCardProps {
  label: string;
  value: number | string;
  description?: string;
  icon: React.ReactNode;
  variant?: KpiVariant;
  selected?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  loading?: boolean;
  /** When true and numeric value is 0, use muted styling. */
  neutralWhenZero?: boolean;
}

// ── Component ──

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  description,
  icon,
  variant = 'default',
  selected = false,
  clickable,
  onClick,
  ariaLabel,
  loading = false,
  neutralWhenZero = false,
}) => {
  const isClickable = clickable ?? !!onClick;
  const vc = VARIANT_COLORS[variant];
  const numericValue = typeof value === 'number' ? value : parseInt(String(value), 10);
  const isZero = !Number.isNaN(numericValue) && numericValue === 0;
  const numberColor = neutralWhenZero && isZero ? vc.numberZero : vc.number;

  const cardSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
    width: '100%',
    bgcolor: selected ? `${appColors.primary.main}08` : appColors.surface.card,
    borderRadius: '12px',
    border: selected
      ? `2px solid ${appColors.primary.main}`
      : `1px solid ${appColors.border.default}`,
    boxShadow: selected ? `0 0 0 3px ${appColors.primary.main}20` : 'none',
    px: { xs: 1.75, lg: 2.25 },
    py: { xs: 1.5, lg: 1.75 },
    minHeight: { xs: 120, lg: 132 },
    transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
    boxSizing: 'border-box' as const,
  };

  const content = (
    <>
      {/* Row 1: icon + label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 0.75 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: vc.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: vc.icon,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.95rem', lg: '1.05rem' },
            lineHeight: 1.3,
            color: appColors.primary.dark,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Row 2: large number */}
      <Box sx={{ textAlign: 'center', mt: 0.25 }}>
        {loading ? (
          <Skeleton
            variant="text"
            width={64}
            height={40}
            sx={{ mx: 'auto', borderRadius: '6px' }}
          />
        ) : (
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', lg: '2rem' },
              lineHeight: 1.1,
              color: numberColor,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value ?? '—'}
          </Typography>
        )}
      </Box>

      {/* Row 3: description */}
      {description && (
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: appColors.text.placeholder,
            textAlign: 'center',
            mt: 0.25,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {description}
        </Typography>
      )}
    </>
  );

  if (isClickable) {
    return (
      <ButtonBase
        onClick={onClick}
        aria-label={ariaLabel || `${label}, ${value}`}
        aria-pressed={selected}
        sx={{
          ...cardSx,
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': {
            borderColor: selected ? appColors.primary.main : appColors.border.hover,
            boxShadow: selected
              ? `0 0 0 3px ${appColors.primary.main}20`
              : appColors.shadow.cardHover,
          },
          '&:focus-visible': {
            outline: `2px solid ${appColors.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        {content}
      </ButtonBase>
    );
  }

  return (
    <Box
      role="group"
      aria-label={ariaLabel || `${label}, ${value}`}
      sx={cardSx}
    >
      {content}
    </Box>
  );
};

export default KpiCard;
